import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {IGroupAccess, IGroupAccessPerStore} from '../../models/group-access';
import {IUser} from '../../models/user/user';
import {
  createStaffAccessColleague,
  setColleaguesUserAccess
} from '../../user-access-store/user-access.actions';
import {Store} from '@ngrx/store';
import {ISharedModalBasic, ISharedModalBasicResponse} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  SharedModalBasicComponent
} from '../../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import {map, startWith, takeUntil, filter, take} from 'rxjs/operators';
import {firstValueFrom, Subject} from 'rxjs';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {
  IModalDismissResult,
  ModalControllerService
} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {
  accessCodeFromRuleHumanIds,
  getPresetRoleNames,
  roleDefaultRuleIds,
  ruleHumanIdsFromAccessCode
} from '../../utils/custom-permissions.utils';
import {
  CustomisePermissionsModalComponent,
  ICustomisePermissionsModalResult
} from '../customise-permissions-modal/customise-permissions-modal.component';
import {RuleHumanID} from '../../models/rule-info.enum';
import {
  selectLastFailedColleagueAccessUpdateUserId,
  selectLastFailedStaffAccessCreateUserId,
  selectUserAccessState
} from '../../user-access-store/user-access.selectors';
import {
  StaffAccessPasswordResetLinkService
} from '../../service/staff-access-password-reset-link/staff-access-password-reset-link.service';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  standalone: false
})
export class AddUserComponent implements OnInit, OnDestroy {
  @Input() defaultUserAccessCodes: IGroupAccess;
  @Input() currentUser: IUser;
  @Input() userLimit: number;
  @Input() freeSpace: {storeId: string; available: number}[];
  @Input() editingUserId: string;
  @Input() currentPermissions: Record<string, IGroupAccessPerStore>;
  @Input() selectedEntries?: string[];
  @Input() newUser = false;
  @Input() storesAccessible: {
    stores: Record<string, string>;
    order: string[];
  } = {stores: {}, order: []};
  @Input() currentStore?: {storeId: string; storeName: string};
  @Input() colleaguesEmails?: string[];
  @Input() returnData?: boolean = false;
  @Input() allowNoAccess = false;
  @Input() preselectedEntryIds: string[] = [];

  groupAccessPerStore: IGroupAccessPerStore = {};
  adminStoreIdsWithSelected: Record<string, {selected: boolean; groupName: string}> = {};
  customRuleSelections: Record<string, RuleHumanID[]> = {};
  private customRoleSwitchStash: Record<string, RuleHumanID[]> = {};
  roleOptionsByEntry: Record<string, Record<string, ISelectOption>> = {};
  roleKeyOrder: string[] = [];
  userForm: FormGroup;
  isLoading = false;
  protected readonly animation = DEFAULT_LOADER_INFINITE;
  filteredStoreIds: string[];
  destroy$ = new Subject<boolean>();
  protected readonly icon = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly adminPermissionsTooltip =
    'Admin users have full access. Permissions cannot be customised for the Admin role.';
  private presetRoleNames: string[] = [];

  constructor(
    private readonly store: Store,
    private formBuilder: FormBuilder,
    private modalController: ModalControllerService,
    private readonly staffAccessPasswordResetLinkService: StaffAccessPasswordResetLinkService
  ) {
    this.userForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      search: new FormControl('')
    });
  }

  get modalTitle(): string {
    if (this.newUser) {
      return 'Add New User';
    }
    if (this.editingUserId) {
      return 'Give Store Access';
    }
    return 'Add Users to Store';
  }

  get modalSubtitle(): string {
    if (this.newUser) {
      return '';
    }
    if (this.editingUserId) {
      return this.editingUserId;
    }
    return this.currentStore?.storeName ?? '';
  }

  get entryLabel(): string {
    if (this.newUser || this.editingUserId) {
      return 'Stores';
    }
    return 'Users';
  }

  get submitLabel(): string {
    if (this.newUser) {
      return 'Create User';
    }
    return this.editingUserId ? 'Update User' : 'Update Store';
  }

  get hasAvailableEntries(): boolean {
    const selectedCount = this.selectedEntries?.length ?? 0;
    return Object.keys(this.storesAccessible.stores).length - selectedCount > 0
      && this.filteredStoreIds.some((entryId: string) => !this.selectedEntries?.includes(entryId));
  }

  get isSubmitDisabled(): boolean {
    if (this.isLoading) {
      return true;
    }
    if (this.newUser && !this.userForm.controls['email'].valid) {
      return true;
    }
    return this.isGroupAccessIncomplete();
  }

  ngOnInit(): void {
    this.adminStoreIdsWithSelected = Object.keys(this.storesAccessible.stores).reduce<
      Record<string, {selected: boolean; groupName: string}>
    >((acc, entryId) => {
      acc[entryId] = {selected: false, groupName: ''};
      return acc;
    }, {});

    const allIds = Object.keys(this.storesAccessible.stores);

    this.userForm.controls.search.valueChanges.pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map((raw: string) => raw.trim().toLowerCase())
    ).subscribe((term: string) => {
      this.filteredStoreIds =
        term === ''
          ? allIds
          : allIds.filter((id: string) => {
            const label = this.getEntryDisplayName(id).toLowerCase();
            return id.toLowerCase().includes(term) || label.includes(term);
          });
    });

    this.filteredStoreIds = allIds;

    this.defaultUserAccessCodes = Object.entries(this.defaultUserAccessCodes)
      .sort(([, valueA], [, valueB]) => {
        const priorityA = valueA.orderPriority ?? Infinity;
        const priorityB = valueB.orderPriority ?? Infinity;
        return priorityA - priorityB;
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as IGroupAccess);

    this.presetRoleNames = getPresetRoleNames(Object.keys(this.defaultUserAccessCodes));
    this.roleKeyOrder = [...this.presetRoleNames, 'Custom'];
    this.applyPreselectedEntries();
  }

  /**
   * Selects preselected entries that still have available slots so the caller can
   * open this modal already focused on a store.
   */
  private applyPreselectedEntries(): void {
    const preselected = new Set(this.preselectedEntryIds ?? []);
    if (preselected.size === 0) {
      return;
    }

    for (const entryId of Object.keys(this.adminStoreIdsWithSelected)) {
      if (!preselected.has(entryId) || this.getAvailableSpace(entryId) < 1) {
        continue;
      }
      this.adminStoreIdsWithSelected[entryId].selected = true;
      this.refreshRoleOptionsForEntry(entryId);
    }
  }

  isEmailFieldInvalid(): boolean {
    const control = this.userForm.get('email');
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getEmailErrorMessage(): string {
    const control = this.userForm.get('email');
    if (!control?.errors) {
      return '';
    }
    if (control.errors['required']) {
      return 'Email is required';
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  onEmailBlur(): void {
    this.userForm.get('email')?.markAsTouched();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  isRowRoleControlsVisible(entryId: string): boolean {
    return !!this.adminStoreIdsWithSelected[entryId]?.selected && this.getAvailableSpace(entryId) > 0;
  }

  isAdminRole(entryId: string): boolean {
    return this.adminStoreIdsWithSelected[entryId]?.groupName === 'Admin';
  }

  getAvailableSpace(entryId: string): number {
    const slot = this.freeSpace.find((space) => space.storeId === entryId);
    return slot?.available ?? 0;
  }

  getEntryDisplayName(entryId: string): string {
    const label = this.storesAccessible.stores[entryId]?.trim();
    return label || entryId;
  }

  getEntrySecondaryLabel(entryId: string): string | null {
    if (this.newUser || this.editingUserId) {
      return null;
    }
    const displayName = this.getEntryDisplayName(entryId);
    if (displayName === entryId) {
      return null;
    }
    return entryId;
  }

  private buildRoleSelectOptions(entryId: string): Record<string, ISelectOption> {
    const selectedGroup = this.adminStoreIdsWithSelected[entryId]?.groupName;
    const options: Record<string, ISelectOption> = {};

    for (const groupName of this.presetRoleNames) {
      if (groupName === 'No Access' && !this.allowNoAccess) {
        continue;
      }
      options[groupName] = {
        name: groupName,
        disabled: false,
        selected: selectedGroup === groupName
      };
    }

    options.Custom = {
      name: 'Custom',
      disabled: false,
      selected: selectedGroup === 'Custom'
    };

    return options;
  }

  private refreshRoleOptionsForEntry(entryId: string): void {
    this.roleOptionsByEntry[entryId] = this.buildRoleSelectOptions(entryId);
  }

  onRoleSelectChange(entryId: string, selected: Record<string, ISelectOption>): void {
    const groupName = Object.keys(selected).find((key) => selected[key].selected);
    if (!groupName || !this.adminStoreIdsWithSelected[entryId]?.selected) {
      return;
    }

    const previousGroupName = this.adminStoreIdsWithSelected[entryId].groupName;
    if (groupName === previousGroupName) {
      if (groupName === 'Custom' && !this.customRuleSelections[entryId]?.length) {
        this.ensureCustomRuleSelectionsForEntry(entryId, previousGroupName);
        this.updateGroupAccessForEntry(entryId);
      }
      return;
    }

    this.adminStoreIdsWithSelected[entryId].groupName = groupName;

    if (groupName === 'Custom') {
      this.ensureCustomRuleSelectionsForEntry(entryId, previousGroupName);
    } else {
      if (previousGroupName === 'Custom' && this.customRuleSelections[entryId]?.length) {
        this.customRoleSwitchStash[entryId] = [...this.customRuleSelections[entryId]];
      }
      delete this.customRuleSelections[entryId];
    }

    this.updateGroupAccessForEntry(entryId);
    this.refreshRoleOptionsForEntry(entryId);
  }

  private ensureCustomRuleSelectionsForEntry(entryId: string, previousGroupName: string): void {
    if (this.customRuleSelections[entryId]?.length) {
      return;
    }

    const stashed = this.customRoleSwitchStash[entryId];
    if (stashed?.length) {
      this.customRuleSelections[entryId] = [...stashed];
      delete this.customRoleSwitchStash[entryId];
      return;
    }

    const existingAccessCode = this.groupAccessPerStore[entryId]?.groupAccessCode;
    if (existingAccessCode) {
      const decoded = ruleHumanIdsFromAccessCode(existingAccessCode);
      if (decoded.length) {
        this.customRuleSelections[entryId] = decoded;
        return;
      }
    }

    if (previousGroupName && previousGroupName !== 'No Access' && previousGroupName !== 'Custom') {
      this.customRuleSelections[entryId] = roleDefaultRuleIds(previousGroupName, this.defaultUserAccessCodes);
      return;
    }

    this.customRuleSelections[entryId] = [];
  }

  async onCustomisePermissionsClick(entryId: string): Promise<void> {
    if (!this.isRowRoleControlsVisible(entryId) || this.isAdminRole(entryId)) {
      return;
    }

    const groupName = this.adminStoreIdsWithSelected[entryId]?.groupName;
    if (!groupName) {
      return;
    }

    const existingCustom = this.customRuleSelections[entryId];
    const initialSelected = existingCustom?.length
      ? existingCustom
      : groupName === 'Custom'
        ? []
        : roleDefaultRuleIds(groupName, this.defaultUserAccessCodes);

    const modal = await this.modalController.create({
      component: CustomisePermissionsModalComponent,
      componentProps: {
        contextLabel: this.getCustomiseContextLabel(entryId),
        initialSelected
      },
      cssClass: ['customise-permissions-modal-css'],
      backdropDismiss: false
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    const result = data as ICustomisePermissionsModalResult | undefined;
    if (!result?.confirmed || !result.selectedRuleIds) {
      return;
    }

    this.customRuleSelections[entryId] = result.selectedRuleIds;
    this.adminStoreIdsWithSelected[entryId].groupName = 'Custom';
    this.updateGroupAccessForEntry(entryId);
    this.refreshRoleOptionsForEntry(entryId);
  }

  onSearchChange(searchValue: string): void {
    this.userForm.controls.search.setValue(searchValue);
  }

  async onCancel(): Promise<void> {
    await this.modalController.dismiss({confirmed: false});
  }

  onSubmit(): void {
    if (this.newUser) {
      void this.createUser();
      return;
    }
    if (this.editingUserId) {
      this.updateUser();
      return;
    }
    void this.updateStore();
  }

  async createUser(): Promise<void> {
    if (!this.returnData) {
      const newEmail = String(this.userForm.value.email).trim();
      const normalizedEmail = newEmail.toLowerCase();
      const existingEmail = this.colleaguesEmails?.find(
        (email: string) => email.toLowerCase() === normalizedEmail
      );
      if (this.currentUser.id === newEmail || this.currentUser.id?.toLowerCase() === normalizedEmail) {
        const ownUserProps: ISharedModalBasic = {
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Cancel',
          modalTitle: 'You Cannot Edit Your Own User',
          modalTitleIcon: this.icon.person,
          modalTitleIconColor: this.iconColourEnum.yellow,
          contextHeading: 'Please contact a fellow admin or Gallix support',
          contextDescription: ['As a safety measure, users cannot edit their own access.']
        };
        const ownUserModal = await this.modalController.create({
          component: SharedModalBasicComponent,
          componentProps: ownUserProps,
          cssClass: ['shared-basic-modal-css-medium']
        });
        await ownUserModal.present();
        await ownUserModal.onDidDismiss();
        return;
      }
      if (existingEmail) {
        const confirmProps: ISharedModalBasic = {
          buttonAccept: true,
          buttonAcceptText: 'Confirm',
          buttonClose: true,
          buttonCloseCross: false,
          buttonCloseText: 'Cancel',
          modalTitle: 'This User Already Exists',
          modalTitleIcon: this.icon.person,
          modalTitleIconColor: this.iconColourEnum.yellow,
          contextHeading: 'You already have access to this user',
          contextDescription: ['Confirm if you want to Edit this user or you can Edit their access in the users tab.']
        };
        const confirmEditModal = await this.modalController.create({
          component: SharedModalBasicComponent,
          componentProps: confirmProps,
          cssClass: ['shared-basic-modal-css-medium']
        });
        await confirmEditModal.present();
        const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await confirmEditModal.onDidDismiss();

        if (modalResponse?.data?.buttonPressed !== 'button-accept') {
          this.isLoading = false;
          return;
        }

        this.isLoading = true;
        this.store.dispatch(
          setColleaguesUserAccess({
            userId: existingEmail,
            editedPermissions: {
              ...(this.currentPermissions?.[existingEmail] ?? {}),
              ...this.groupAccessPerStore
            },
            merge: true,
            newUser: false
          })
        );
        await firstValueFrom(
          this.store.select(selectUserAccessState).pipe(
            map((state) => state.isLoading),
            filter((loading: boolean) => !loading),
            take(1)
          )
        );
        const failedUpdateUserId = await firstValueFrom(
          this.store.select(selectLastFailedColleagueAccessUpdateUserId).pipe(take(1))
        );
        this.isLoading = false;
        if (failedUpdateUserId?.toLowerCase() === existingEmail.toLowerCase()) {
          await this.presentColleagueUpdateFailureModal(existingEmail);
          return;
        }
        await this.modalController.dismiss({existingEmail});
        return;
      }

      this.isLoading = true;
      this.store.dispatch(
        createStaffAccessColleague({
          userId: normalizedEmail,
          editedPermissions: this.groupAccessPerStore
        })
      );

      await firstValueFrom(
        this.store.select(selectUserAccessState).pipe(
          map((state) => state.isLoading),
          filter((loading: boolean) => !loading),
          take(1)
        )
      );

      const failedUserId = await firstValueFrom(
        this.store.select(selectLastFailedStaffAccessCreateUserId).pipe(take(1))
      );

      this.isLoading = false;

      if (failedUserId?.toLowerCase() === normalizedEmail) {
        return;
      }

      const passwordResetLink = this.staffAccessPasswordResetLinkService.consumePasswordResetLink(normalizedEmail);
      if (!passwordResetLink) {
        return;
      }

      await this.modalController.dismiss({newUserEmail: normalizedEmail, passwordResetLink});
    } else {
      const newEmail = String(this.userForm.value.email).toLowerCase();
      await this.modalController.dismiss({newUserEmail: newEmail, editedPermissions: this.groupAccessPerStore});
    }
  }

  /**
   * Informs the user that an existing colleague's store access could not be saved, leaving the form open so the
   * assignment can be retried.
   *
   * @param {string} userId - Email of the colleague whose access update failed.
   */
  private async presentColleagueUpdateFailureModal(userId: string): Promise<void> {
    const failureProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Continue',
      modalTitle: 'Access Not Updated',
      modalTitleIcon: this.icon.alertBox,
      modalTitleIconColor: this.iconColourEnum.red,
      contextHeading: 'We could not save the store access for this user',
      contextDescription: [
        `The changes for ${userId} were not saved.`,
        'Please try again or contact Gallix support.'
      ]
    };
    const failureModal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps: failureProps,
      cssClass: ['shared-basic-modal-css-medium']
    });
    await failureModal.present();
    await failureModal.onDidDismiss();
  }

  updateUser(): void {
    void this.addUserAccessOnDatabase(this.editingUserId, {
      ...this.currentPermissions[this.editingUserId],
      ...this.groupAccessPerStore
    });
    void this.modalController.dismiss();
  }

  async updateStore(): Promise<void> {
    this.isLoading = true;
    const updatePromises: Promise<void>[] = [];

    for (const userId of Object.keys(this.groupAccessPerStore)) {
      const updatedPermissions: IGroupAccessPerStore = {
        [this.currentStore.storeId]: {
          ...this.groupAccessPerStore[userId],
          storeName: this.currentStore.storeName
        }
      };
      const permissionsToUpdate = {...updatedPermissions, ...this.currentPermissions[userId]};
      updatePromises.push(this.addUserAccessOnDatabase(userId, permissionsToUpdate));
    }
    await Promise.all(updatePromises);
    this.isLoading = false;
    await this.modalController.dismiss();
  }

  async addUserAccessOnDatabase(
    newEmail: string,
    groupAccessPerStore: IGroupAccessPerStore
  ): Promise<void> {
    this.store.dispatch(
      setColleaguesUserAccess({
        userId: newEmail,
        editedPermissions: groupAccessPerStore,
        merge: !this.newUser,
        newUser: this.newUser
      })
    );
  }

  isGroupAccessIncomplete(): boolean {
    const entries = Object.values(this.groupAccessPerStore);
    if (entries.length === 0) {
      return true;
    }
    return !entries.every(
      (entry: {
        storeName: string,
        groupName: string,
        groupAccessCode: number
      }) => entry.storeName && entry.groupName && entry.groupAccessCode !== null && entry.groupAccessCode !== undefined
    );
  }

  onStoreSelect(entryId: string): void {
    const isSelected = (this.adminStoreIdsWithSelected[entryId].selected =
      !this.adminStoreIdsWithSelected[entryId].selected);
    if (!isSelected) {
      delete this.groupAccessPerStore[entryId];
      delete this.customRuleSelections[entryId];
      delete this.roleOptionsByEntry[entryId];
      this.adminStoreIdsWithSelected[entryId].groupName = '';
    } else {
      this.adminStoreIdsWithSelected[entryId].groupName = '';
      delete this.groupAccessPerStore[entryId];
      delete this.customRuleSelections[entryId];
      this.refreshRoleOptionsForEntry(entryId);
    }
  }

  private getCustomiseContextLabel(entryId: string): string {
    if (this.editingUserId) {
      return `${this.editingUserId} → ${this.getEntryDisplayName(entryId)}`;
    }
    if (this.newUser) {
      return this.getEntryDisplayName(entryId);
    }
    return `${this.getEntryDisplayName(entryId)} → ${this.currentStore?.storeName ?? ''}`;
  }

  private getStoreNameForEntry(entryId: string): string {
    if (!this.newUser && !this.editingUserId) {
      return this.currentStore?.storeName ?? '';
    }
    return this.storesAccessible.stores[entryId] ?? entryId;
  }

  private resolveAccessCodeForEntry(entryId: string): number {
    const groupName = this.adminStoreIdsWithSelected[entryId]?.groupName;
    if (!groupName) {
      return 0;
    }
    if (groupName === 'Custom') {
      const customIds = this.customRuleSelections[entryId];
      if (customIds?.length) {
        return accessCodeFromRuleHumanIds(customIds);
      }
      return 0;
    }
    return this.defaultUserAccessCodes[groupName]?.accessCode ?? 0;
  }

  private updateGroupAccessForEntry(entryId: string): void {
    const groupName = this.adminStoreIdsWithSelected[entryId]?.groupName;
    if (!groupName) {
      return;
    }

    this.groupAccessPerStore[entryId] = {
      storeName: this.getStoreNameForEntry(entryId),
      groupName,
      groupAccessCode: this.resolveAccessCodeForEntry(entryId)
    };
  }
}
